import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { moodsAPI } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart, 
  faArrowLeft, 
  faCalendar, 
  faClock,
  faChartLine 
} from '@fortawesome/free-solid-svg-icons';
import 'react-toastify/dist/ReactToastify.css';
import { getUser } from '../utils/auth';

// Updated to match backend expectations
const MOODS = [
  { value: 'calm', label: 'Calm', emoji: '😌', color: '#22c55e' },
  { value: 'motivated', label: 'Motivated', emoji: '💪', color: '#84cc16' },
  { value: 'neutral', label: 'Neutral', emoji: '😐', color: '#eab308' },
  { value: 'tired', label: 'Tired', emoji: '😴', color: '#f97316' },
  { value: 'heavy', label: 'Heavy', emoji: '😔', color: '#ef4444' },
];

const MyBestie = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [selectedMood, setSelectedMood] = useState(null);
  const [loading, setLoading] = useState(false);
  const [todayMoods, setTodayMoods] = useState([]);
  const [moodHistory, setMoodHistory] = useState([]);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [showClockOutPrompt, setShowClockOutPrompt] = useState(false);

  useEffect(() => {
    fetchTodayMoods();
    fetchMoodHistory();
  }, []);

  const fetchTodayMoods = async () => {
    try {
      console.log('Fetching today\'s moods...');
      const response = await moodsAPI.getTodayMoods();
      console.log('Today\'s moods response:', response.data);
      const moods = response.data?.moods || [];
      setTodayMoods(moods);
      
      // Check if user has clocked in today (for enterprise users)
      const clockIn = moods.find(m => m.mood_time === 'clock-in');
      const clockOut = moods.find(m => m.mood_time === 'clock-out');
      setIsClockedIn(!!clockIn && !clockOut);
    } catch (error) {
      console.error('Error fetching today\'s moods:', error);
      setTodayMoods([]);
    }
  };

  const fetchMoodHistory = async () => {
    try {
      console.log('Fetching mood history...');
      const response = await moodsAPI.getMoods({ limit: 7 });
      console.log('Mood history response:', response.data);
      setMoodHistory(response.data?.moods || []);
    } catch (error) {
      console.error('Error fetching mood history:', error);
      setMoodHistory([]);
    }
  };

  const handleLogMood = async () => {
    if (!selectedMood) {
      toast.warning('Please select a mood');
      return;
    }

    console.log('🔍 DEBUG: User object:', user);
    console.log('🔍 DEBUG: User type:', user?.user_type);
    console.log('🔍 DEBUG: Today moods count:', todayMoods.length);
    console.log('🔍 DEBUG: Is clocked in:', isClockedIn);

    // For enterprise users with special clock-in/out logic
    if (user?.user_type === 'enterprise') {
      console.log('✅ User is enterprise type');
      
      // If no moods logged today, this is clock-in
      if (todayMoods.length === 0) {
        console.log('📍 First mood of day - calling handleClockIn');
        await handleClockIn();
        return;
      }
      
      // If already clocked in but not clocked out, ask if this is clock-out
      if (isClockedIn) {
        console.log('📍 Already clocked in - showing clock-out prompt');
        setShowClockOutPrompt(true);
        return;
      }
      
      console.log('📍 Already clocked out - logging normal mood');
    } else {
      console.log('ℹ️ User is NOT enterprise type, logging normal mood');
    }

    // For non-enterprise users or if already clocked out, log normal mood
    await logNormalMood();
  };

  // Clock-in handler for enterprise users
  const handleClockIn = async () => {
    setLoading(true);
    try {
      const response = await moodsAPI.clockIn({
        mood_type: selectedMood.value
      });
      
      toast.success(`Clocked in: ${selectedMood.emoji} ${selectedMood.label}`, {
        icon: '🏢'
      });
      setSelectedMood(null);
      setIsClockedIn(true);
      
      await fetchTodayMoods();
      await fetchMoodHistory();
    } catch (error) {
      console.error('Error clocking in:', error);
      toast.error(error.response?.data?.error || 'Failed to clock in');
    } finally {
      setLoading(false);
    }
  };

  // Clock-out handler for enterprise users
  const handleClockOut = async () => {
    setLoading(true);
    setShowClockOutPrompt(false);
    
    try {
      const response = await moodsAPI.clockOut({
        mood_type: selectedMood.value
      });
      
      toast.success(`Clocked out: ${selectedMood.emoji} ${selectedMood.label}`, {
        icon: '🏁'
      });
      setSelectedMood(null);
      setIsClockedIn(false);
      
      await fetchTodayMoods();
      await fetchMoodHistory();
    } catch (error) {
      console.error('Error clocking out:', error);
      toast.error(error.response?.data?.error || 'Failed to clock out');
    } finally {
      setLoading(false);
    }
  };

  // Log normal mood (not clock-in/out)
  const logNormalMood = async () => {
    setLoading(true);
    try {
      console.log('Logging mood:', { mood: selectedMood.value });
      
      const response = await moodsAPI.logMood({
        mood: selectedMood.value
      });
      
      console.log('Mood logged successfully:', response.data);
      toast.success(`Mood logged: ${selectedMood.emoji} ${selectedMood.label}`);
      
      setSelectedMood(null);
      
      await fetchTodayMoods();
      await fetchMoodHistory();
    } catch (error) {
      console.error('Error logging mood:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to log mood');
    } finally {
      setLoading(false);
    }
  };

  // Cancel clock-out and log as normal mood
  const handleCancelClockOut = async () => {
    setShowClockOutPrompt(false);
    await logNormalMood();
  };

  const getMoodEmoji = (moodValue) => {
    const mood = MOODS.find(m => m.value === moodValue);
    return mood ? mood.emoji : '😐';
  };

  const getMoodColor = (moodValue) => {
    const mood = MOODS.find(m => m.value === moodValue);
    return mood ? mood.color : '#999';
  };

  const getMoodLabel = (moodValue) => {
    const mood = MOODS.find(m => m.value === moodValue);
    return mood ? mood.label : moodValue;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getMoodTimeLabel = (moodTime) => {
    const labels = {
      'clock-in': 'Clock In',
      'clock-out': 'Clock Out',
      'morning': 'Morning',
      'afternoon': 'Afternoon',
      'evening': 'Evening',
      'night': 'Night'
    };
    return labels[moodTime] || moodTime;
  };

  return (
    <div style={{
      minHeight: '100vh',
      padding: '2rem'
    }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard')} 
          style={{
            background: 'none',
            border: '1px solid #FFB6C1',
            color: '#FFB6C1',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '2rem'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>

        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
            <FontAwesomeIcon icon={faHeart} /> My Bestie - Mood Tracker
          </h1>
          <p style={{ color: '#999' }}>
            Hey {user?.full_name || 'there'}! How are you feeling right now?
          </p>
        </div>

        {/* Log New Mood */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginBottom: '1.5rem', fontSize: '1.3rem' }}>Log Your Mood</h2>
          
          {/* Mood Selection */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {MOODS.map((mood) => (
              <button
                key={mood.value}
                onClick={() => setSelectedMood(mood)}
                style={{
                  padding: '1.5rem 1rem',
                  backgroundColor: selectedMood?.value === mood.value ? mood.color : '#0a0a0a',
                  border: `2px solid ${selectedMood?.value === mood.value ? mood.color : '#333'}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{mood.emoji}</div>
                <div style={{ 
                  fontSize: '0.9rem',
                  color: selectedMood?.value === mood.value ? '#000' : '#fff',
                  fontWeight: '600'
                }}>
                  {mood.label}
                </div>
              </button>
            ))}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleLogMood}
            disabled={loading || !selectedMood}
            style={{
              width: '100%',
              padding: '1rem',
              backgroundColor: selectedMood ? '#FFB6C1' : '#333',
              border: 'none',
              borderRadius: '8px',
              color: selectedMood ? '#000' : '#666',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading || !selectedMood ? 'not-allowed' : 'pointer',
              opacity: loading || !selectedMood ? 0.7 : 1
            }}
          >
            {loading ? 'Logging...' : 'Log Mood'}
          </button>
        </div>

        {/* Today's Moods */}
        {todayMoods.length > 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem'
          }}>
            <h2 style={{ 
              marginBottom: '1.5rem', 
              fontSize: '1.3rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FontAwesomeIcon icon={faCalendar} style={{ color: '#FFB6C1' }} />
              Today's Mood Log
            </h2>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              {todayMoods.map((moodEntry, index) => (
                <div 
                  key={moodEntry.id || index}
                  style={{
                    backgroundColor: '#0a0a0a',
                    border: `1px solid ${getMoodColor(moodEntry.mood_type)}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                >
                  <div style={{ fontSize: '2rem' }}>{getMoodEmoji(moodEntry.mood_type)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '600', 
                      marginBottom: '0.25rem', 
                      textTransform: 'capitalize',
                      color: getMoodColor(moodEntry.mood_type)
                    }}>
                      {getMoodLabel(moodEntry.mood_type)}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                      {getMoodTimeLabel(moodEntry.mood_time)}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '0.85rem', 
                    color: '#FFB6C1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <FontAwesomeIcon icon={faClock} />
                    {formatTime(moodEntry.created_at)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent History */}
        {moodHistory.length > 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '2rem'
          }}>
            <h2 style={{ 
              marginBottom: '1.5rem', 
              fontSize: '1.3rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FontAwesomeIcon icon={faChartLine} style={{ color: '#FFB6C1' }} />
              Recent History (Last 7 Days)
            </h2>
            
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {moodHistory.map((moodEntry, index) => (
                <div 
                  key={moodEntry.id || index}
                  style={{
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    opacity: index === 0 ? 1 : 0.7
                  }}
                >
                  <div style={{ fontSize: '1.5rem' }}>{getMoodEmoji(moodEntry.mood_type)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.9rem', color: '#999' }}>
                      {formatDate(moodEntry.created_at)}
                    </div>
                    <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                      {getMoodLabel(moodEntry.mood_type)} • {getMoodTimeLabel(moodEntry.mood_time)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {todayMoods.length === 0 && moodHistory.length === 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '3rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <h3 style={{ color: '#FFB6C1', marginBottom: '0.5rem' }}>No Mood Data Yet</h3>
            <p style={{ color: '#999' }}>
              Start logging your moods to track your emotional wellbeing over time.
            </p>
          </div>
        )}
      </div>

      {/* Clock-Out Prompt Modal */}
      {showClockOutPrompt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '2px solid #FFB6C1',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏁</div>
            <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>End Your Workday?</h3>
            <p style={{ color: '#999', marginBottom: '2rem' }}>
              Are you clocking out for the day, or just updating your mood?
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={handleCancelClockOut}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #666',
                  borderRadius: '8px',
                  color: '#999',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  opacity: loading ? 0.7 : 1
                }}
              >
                Just Mood Update
              </button>
              <button
                onClick={handleClockOut}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#FFB6C1',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#000',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? 'Clocking Out...' : 'Yes, Clock Out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBestie;
